import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Rating,
  BadgeCollection,
  TockBadge,
  TranslationProvider,
  ReservationButtonList,
  Link,
  utils,
} from '@resy/ui-kitchen';
import openTockUrl from '../../../../../ui-kitchen/src/utils/openTockUrl';
import { openTockExperienceOptions } from '../../../../../ui-kitchen/src/reducers/tockExperienceSlice';

import './Popover.scss';

const Popover = ({
  isMobile, selectedDate, selectedPartySize, selectedTime, venue,
}) => {
  const dispatch = useDispatch();
  const isTockInventory = venue.isTockInventory === true;
  const hasTockExperiences = isTockInventory && (venue.experiences?.length > 0);
  const { priceRangeToString } = utils;
  const onReservationSelect = (reservation) => {
    if (hasTockExperiences && reservation?.time) {
      dispatch(openTockExperienceOptions({
        experiences: venue.experiences,
        isMobile,
        selectedTime: reservation.time,
        selectedDate,
        selectedSeats: selectedPartySize,
        venueName: venue.name,
        venueId: venue.id,
      }));
      return;
    }

    const reservationConfig = {};
    reservationConfig.venueId = venue.id;
    reservationConfig.src = 'resy.com-info-box';
    reservationConfig.date = selectedDate;
    reservationConfig.seats = selectedPartySize;

    if (reservation) {
      reservationConfig.tableConfigId = reservation.token;
    }

    resyWidget.openModal(reservationConfig);
  };

  const venueLink = isTockInventory
    ? venue.tockBusinessPageUrl
    : `/cities/${venue.location.urlSlug}/venues/${venue.urlSlug}?date=${moment(selectedDate).format('YYYY-MM-DD')}&seats=${selectedPartySize}&time=${selectedTime}`;

  const renderCollections = () => {
    if (isTockInventory) {
      return null;
    }

    if (venue.collections && venue.collections.length > 0) {
      return (
        <div className="Popover__collections">
          <BadgeCollection
            badges={venue.collections}
            showLabels={false}
          />
        </div>
      );
    }
    return null;
  };

  const renderAvailability = () => {
    if (venue.slots.length === 0 && venue.availableServiceTypes.length === 0 && !(isTockInventory && venue.tockNotifyUrl)) {
      return (
        <TranslationProvider translationKey="no_availability_for_party_message" translationValues={{ partySize: selectedPartySize }}>
          <div className="SearchResult__no-availability">{`Sorry, we don't currently have any tables available for ${selectedPartySize}.`}</div>
        </TranslationProvider>
      );
    }

    return (
      <ReservationButtonList
        activeNotifications={venue.activeNotifications}
        experiences={venue.experiences}
        featureRecaptcha={venue.featureRecaptcha}
        hasTockExperiences={hasTockExperiences}
        isMobile={isMobile}
        isTockInventory={isTockInventory}
        selectedDate={selectedDate}
        tockBusinessPageUrl={venue.tockBusinessPageUrl}
        tockBusinessSearchUrl={venue.tockBusinessSearchUrl}
        tockNotifyUrl={venue.tockNotifyUrl}
        limit={2}
        onReservationSelect={onReservationSelect}
        partySize={selectedPartySize}
        reservations={venue.slots}
        venueId={venue.id}
        venueName={venue.name}
      />
    );
  };

  const stopPropagation = (popover) => {
    if (popover) {
      google.maps.OverlayView.preventMapHitsFrom(popover);
    }
  };

  return (
    <div
      className={cx('Popover', { 'Popover--with-tock': isTockInventory })}
      ref={stopPropagation}
    >
      <div className="Popover__image-and-content">
        <div className="Popover__image" style={venue.image ? { backgroundImage: `url(${venue.image})` } : null} />
        <div className="Popover__content">
          <Link
            className="Popover__venue-link"
            href={venueLink}
            target="_blank"
            rel="noopener"
            aria-label={venue.name}
            variant="article"
            onClick={(e) => {
              if (isTockInventory) {
                e.preventDefault();
                e.stopPropagation();
                openTockUrl(venueLink, dispatch);
              }
            }}
          >
            <h4 className="Popover__venue-name">{venue.name}</h4>
          </Link>
          {!isTockInventory && (
            <div className="Popover__rating-and-badges">
              <Rating scale={5} score={venue.rating} />
              {renderCollections()}
            </div>
          )}
          <div className="Popover__metadata">
            <div className="cuisine">{venue.cuisine}</div>
            <span className="separator">&middot;</span>
            <div className="neighborhood">{venue.location.neighborhood || venue.location.name}</div>
            <span className="separator">&middot;</span>
            <div className="price">{priceRangeToString(venue.priceRange, venue.currency)}</div>
          </div>
        </div>
      </div>
      {isTockInventory && <TockBadge />}
      <div className="Popover__availability">
        {renderAvailability()}
      </div>
    </div>
  );
};

Popover.propTypes = {
  isMobile: PropTypes.bool,
  venue: PropTypes.shape({
    activeNotifications: PropTypes.shape({
      [PropTypes.number]: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    availableServiceTypes: PropTypes.arrayOf(PropTypes.string),
    collections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]),
      file_name: PropTypes.string,
      image: PropTypes.string,
      short_name: PropTypes.string,
    })),
    cuisine: PropTypes.string.isRequired,
    experiences: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      inventory: PropTypes.shape({
        available: PropTypes.number,
      }),
      name: PropTypes.string.isRequired,
    })),
    featureRecaptcha: PropTypes.bool,
    id: PropTypes.number.isRequired,
    image: PropTypes.string,
    location: PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      neighborhood: PropTypes.string.isRequired,
      urlSlug: PropTypes.string.isRequired,
    }),
    name: PropTypes.string.isRequired,
    objectID: PropTypes.string.isRequired,
    priceRange: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    isTockInventory: PropTypes.bool,
    tockBusinessPageUrl: PropTypes.string,
    tockBusinessSearchUrl: PropTypes.string,
    tockNotifyUrl: PropTypes.string,
    rating: PropTypes.number,
    slots: PropTypes.arrayOf(PropTypes.object).isRequired,
    urlSlug: PropTypes.string.isRequired,
  }).isRequired,
  selectedDate: PropTypes.string.isRequired,
  selectedPartySize: PropTypes.number.isRequired,
  selectedTime: PropTypes.string,
};

Popover.defaultProps = {
  isMobile: false,
  selectedTime: 'all-day',
};

export default Popover;
